import React, { useState, useRef } from 'react';
import { cn } from '../../../../lib/utils';
import { useDragSort } from '../../hooks/useDragSort';

/** Inline-editable text (single click to edit) */
function InlineEdit({ value, onChange, className, placeholder = '...' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft !== value) onChange(draft.trim());
    else setDraft(value);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setDraft(value); } }}
        onClick={(e) => e.stopPropagation()}
        className={cn('rounded border border-indigo-400 bg-white px-1.5 py-0.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500 min-w-0 flex-1', className)}
      />
    );
  }

  return (
    <span
      className={cn('cursor-text truncate flex-1 text-sm font-medium text-slate-700 hover:text-indigo-600', className)}
      title="کلیک برای ویرایش"
      onClick={(e) => { e.stopPropagation(); setEditing(true); setDraft(value); }}
    >
      {value || <span className="text-slate-400">{placeholder}</span>}
    </span>
  );
}

export default function DesignerSidebar({
  embedded = false,
  collapsed,
  onToggleCollapse,
  jobs = [],
  selectedJobId,
  onJobChange,
  reports,
  selectedReportId,
  onSelectReport,
  onAddReport,
  onDeleteReport,
  onDuplicateReport,
  onCopyReportToJob,
  onRenameReport,
  onReorderReports,
  onToggleReportVisible,
  onToggleMainDashboard,
  showCustomerTarget = true,
  onShowCustomerTarget,
  defaultReportId,
  onDefaultReportChange,
  useCustomAsMainDashboard = false,
  onUseCustomAsMainDashboard,
  mainDashboardReportId,
  onMainDashboardReportChange,
  isDirty,
  onSave,
  saving,
  saveError,
}) {
  const { overIdx, dragProps } = useDragSort(reports, onReorderReports);

  const body = (
    <>
      {/* Job selector (from settings.job) */}
      <div className="px-3 pt-3 pb-2 shrink-0">
        <label className="mb-1 block text-xs font-bold text-slate-400">نقش / شغل</label>
        <select
          className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-300"
          value={selectedJobId ?? ''}
          onChange={(e) => onJobChange?.(Number(e.target.value))}
          disabled={!jobs?.length}
        >
          {!jobs?.length && <option value="">در حال بارگذاری…</option>}
          {(jobs || []).map((j) => (
            <option key={j.id} value={j.id}>
              {j.name || `شغل ${j.id}`} (id={j.id})
            </option>
          ))}
        </select>
      </div>

      {/* Defaults + main dashboard */}
      <div className="mx-3 mb-2 rounded-lg border border-slate-200 bg-white p-2.5 space-y-2.5 shrink-0">
        <div>
          <label className="mb-1 block text-[11px] font-bold text-slate-400">تب پیش‌فرض در /گزارشات</label>
          <select
            className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            value={defaultReportId || ''}
            onChange={(e) => onDefaultReportChange?.(e.target.value)}
          >
            {reports.map((r) => (
              <option key={r.id} value={r.id}>{r.name || 'بدون نام'}</option>
            ))}
          </select>
        </div>

        <div className="border-t border-slate-100 pt-2 space-y-2">
          <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="accent-indigo-600 mt-0.5"
              checked={useCustomAsMainDashboard === true}
              onChange={(e) => onUseCustomAsMainDashboard?.(e.target.checked)}
            />
            <span>
              <span className="font-semibold">داشبورد اصلی برای این نقش</span>
              <span className="block text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                صفحه اصلی (/) همان گزارش انتخابی را نشان می‌دهد. همه بخش‌ها در طراح گزارش ساخته می‌شوند.
              </span>
            </span>
          </label>
          {useCustomAsMainDashboard && (
            <div>
              <label className="mb-1 block text-[11px] font-bold text-slate-400">گزارش داشبورد اصلی</label>
              <select
                className="w-full rounded-lg border border-indigo-200 bg-indigo-50/40 px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                value={mainDashboardReportId || ''}
                onChange={(e) => onMainDashboardReportChange?.(e.target.value)}
              >
                {reports.map((r) => (
                  <option key={r.id} value={r.id} disabled={r.visible === false}>
                    {r.name || 'بدون نام'}{r.visible === false ? ' (مخفی)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Reports list */}
      <div className="flex items-center justify-between px-3 py-1.5 shrink-0">
        <span className="text-xs font-bold text-slate-400">فهرست گزارشات</span>
        <button
          type="button"
          onClick={onAddReport}
          className="rounded-md bg-indigo-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-indigo-700"
        >
          + افزودن
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-1 min-h-0">
        {reports.length === 0 && (
          <p className="text-center py-6 text-xs text-slate-400">گزارشی وجود ندارد</p>
        )}
        {reports.map((report, index) => (
          <div
            key={report.id}
            {...dragProps(index)}
            className={cn(
              'group rounded-lg border bg-white transition-all duration-150 cursor-pointer select-none',
              selectedReportId === report.id
                ? 'border-indigo-400 shadow-sm'
                : 'border-slate-200 hover:border-indigo-200',
              overIdx === index && 'border-dashed border-indigo-400 bg-indigo-50',
              !report.visible && 'opacity-50',
            )}
            onClick={() => onSelectReport(report.id)}
          >
            <div className="flex items-center gap-1.5 px-2 py-2">
              <span
                className="shrink-0 text-slate-300 group-hover:text-slate-400 cursor-grab active:cursor-grabbing text-base leading-none"
                title="بکشید برای مرتب‌سازی"
              >
                ⠿
              </span>

              <span className="shrink-0 flex h-4 w-4 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-500">
                {index + 1}
              </span>

              <InlineEdit
                value={report.name}
                onChange={(name) => onRenameReport(report.id, name)}
                placeholder="نام گزارش"
              />

              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleReportVisible(report.id); }}
                className={cn(
                  'shrink-0 text-sm leading-none transition-opacity',
                  report.visible !== false ? 'opacity-80 hover:opacity-100' : 'opacity-30 hover:opacity-60',
                )}
                title={report.visible !== false ? 'مخفی کردن' : 'نمایش'}
              >
                {report.visible !== false ? '👁' : '🙈'}
              </button>

              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDuplicateReport?.(report.id); }}
                className="shrink-0 rounded px-1 text-[11px] font-bold text-slate-400 hover:bg-indigo-50 hover:text-indigo-700"
                title="تکثیر گزارش"
              >
                ⧉
              </button>
              {(jobs || []).length > 1 && (
                <select
                  aria-label="کپی به نقش دیگر"
                  defaultValue=""
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const target = e.target.value;
                    e.target.value = '';
                    if (target) onCopyReportToJob?.(report.id, Number(target));
                  }}
                  className="max-w-[72px] rounded border border-slate-200 bg-white px-0.5 py-0.5 text-[10px] text-slate-500"
                  title="کپی به نقش دیگر"
                >
                  <option value="">نقش</option>
                  {(jobs || [])
                    .filter((j) => Number(j.id) !== Number(selectedJobId))
                    .map((j) => (
                      <option key={j.id} value={j.id}>{j.name || j.id}</option>
                    ))}
                </select>
              )}

              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDeleteReport(report.id); }}
                className="shrink-0 text-slate-300 hover:text-red-500 text-sm leading-none transition-colors"
                title="حذف"
              >
                ✕
              </button>
            </div>

            <label
              className="flex items-center gap-1.5 border-t border-slate-100 px-2 py-1 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="radio"
                name="default-report"
                checked={defaultReportId === report.id}
                onChange={(e) => {
                  e.stopPropagation();
                  onDefaultReportChange?.(report.id);
                }}
                className="accent-indigo-600 cursor-pointer"
              />
              <span className="text-xs text-slate-500">پیش‌فرض گزارشات</span>
            </label>
            {useCustomAsMainDashboard && (
              <label
                className="flex items-center gap-1.5 border-t border-slate-100 px-2 py-1 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="radio"
                  name="main-dashboard-report"
                  checked={mainDashboardReportId === report.id}
                  onChange={(e) => {
                    e.stopPropagation();
                    onMainDashboardReportChange?.(report.id);
                    onToggleMainDashboard?.(report.id, true);
                  }}
                  className="accent-indigo-600 cursor-pointer"
                />
                <span className="text-xs text-indigo-600 font-medium">داشبورد اصلی</span>
              </label>
            )}
          </div>
        ))}
      </div>

      {/* Save button */}
      <div className="shrink-0 border-t border-slate-200 p-3">
        {saveError && (
          <p
            className="mb-2 whitespace-pre-line rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-xs leading-relaxed text-red-700"
            role="alert"
          >
            {saveError}
          </p>
        )}
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !isDirty}
          className={cn(
            'w-full rounded-lg py-2 text-sm font-semibold transition-all',
            isDirty && !saving
              ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed',
          )}
        >
          {saving ? 'در حال ذخیره...' : isDirty ? '💾 ذخیره تغییرات' : '✓ ذخیره شد'}
        </button>
      </div>
    </>
  );

  // Embedded inside DesignerSidePanel — fill parent, no collapse chrome
  if (embedded) {
    return (
      <div className="flex flex-col h-full min-h-0 overflow-hidden bg-slate-50 rtl">
        {body}
      </div>
    );
  }

  return (
    <aside
      className={cn(
        'flex flex-col border-l border-slate-200 bg-slate-50 transition-all duration-200 overflow-hidden rtl',
        collapsed ? 'w-12' : 'w-[260px]',
      )}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-2 py-2 shrink-0">
        {!collapsed && (
          <span className="text-xs font-bold text-slate-500 pr-1">طراح گزارش</span>
        )}
        <button
          type="button"
          onClick={onToggleCollapse}
          className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500 transition-colors shrink-0 mr-auto"
          title={collapsed ? 'باز کردن پانل' : 'بستن پانل'}
        >
          {collapsed ? '«' : '»'}
        </button>
      </div>

      {collapsed && (
        <div className="flex flex-col items-center gap-2 py-3">
          <button onClick={onAddReport} title="افزودن گزارش"
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700">
            +
          </button>
          <button onClick={onSave} disabled={!isDirty || saving} title="ذخیره"
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-200 text-slate-600 text-base hover:bg-slate-300 disabled:opacity-40">
            💾
          </button>
        </div>
      )}

      {!collapsed && body}
    </aside>
  );
}
